import React from 'react';
import { GiftIcon } from 'lucide-react';
import { bonuses, problemCards, volumes } from '../data/content';
import { HeroSection } from '../components/HeroSection';
import { ProblemCard } from '../components/ProblemCard';
import { ProductIntro } from '../components/ProductIntro';
import { VolumeCard } from '../components/VolumeCard';
import { InsideLookSection } from '../components/InsideLookSection';
import { BonusCard } from '../components/BonusCard';
import { BenefitsGrid } from '../components/BenefitsGrid';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { TeachingSituations } from '../components/TeachingSituations';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { FAQAccordion } from '../components/FAQAccordion';
import { FinalCTA } from '../components/FinalCTA';
import { Footer } from '../components/Footer';
import { PurchasePopup } from '../components/PurchasePopup';
import { CtaButton } from '../components/CtaButton';
type LandingPageProps = {
  guaranteeEnabled: boolean;
  testimonialsEnabled: boolean;
  bonusReferencePricesEnabled: boolean;
  salesPopupEnabled: boolean;
};
export function LandingPage({
  guaranteeEnabled,
  testimonialsEnabled,
  bonusReferencePricesEnabled,
  salesPopupEnabled
}: LandingPageProps) {
  return <div className="min-h-screen w-full bg-outer font-body" data-id="element-225" data-mp="3i9ev33kunfcf">
      <main className="mx-auto w-full max-w-[768px] overflow-hidden bg-white" data-id="element-226" data-mp="3i9571ljd7i9d">
        <HeroSection guaranteeEnabled={guaranteeEnabled} data-id="element-227" data-mp="3n76o60x8d7i7" />

        <section className="space-y-2.5 bg-surface px-4 py-4 md:px-12 md:py-6" data-id="element-228" data-mp="3ract958lwsn3">
          {problemCards.map(c => <ProblemCard key={c.title} {...c} data-id="element-229" data-mp="41z7t20ks59" />)}
        </section>

        <ProductIntro data-id="element-230" data-mp="1wwbvknosmbol" />

        <section className="bg-white px-4 pb-6 md:px-12 md:pb-8" data-id="element-231" data-mp="1wx54nvb85d9w">
          <h2 className="text-center font-heading text-[24px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-navy md:text-[32px]" data-id="element-232" data-mp="21v6ju55j4sg3">
            What's Included
          </h2>
          <div className="mt-3 space-y-2.5" data-id="element-233" data-mp="357x59tl5zwol">
            {volumes.map(({
            number,
            ...v
          }) => <VolumeCard key={number} label={`Volume ${number}`} {...v} data-id="element-234" data-mp="309c6zzph3n2s" />)}
          </div>
          <div className="mt-5 text-center" data-id="element-235" data-mp="397nsxofe9wow">
            <p className="font-heading text-[14px] font-extrabold uppercase leading-tight text-navy md:text-[16px]" data-id="element-236" data-mp="396mldtxl7sbx">
              240 pages of volumes — plus 5 bonuses free today.
            </p>
            <div className="mt-2.5" data-id="element-237" data-mp="3j4slc5zu2oe5">
              <CtaButton mode="scroll" subtext="Unlock today's special launch price" data-id="element-238" data-mp="3j3r5zpbsbfqm">
                Yes! Give me all 6 volumes + 5 bonuses
              </CtaButton>
            </div>
          </div>
        </section>

        <InsideLookSection data-id="element-239" data-mp="aq1szrbfx19b" />

        <section className="bg-navy px-4 py-6 md:px-12 md:py-8" data-id="element-240" data-mp="ap8v7fuglm3b">
          <h2 className="flex items-center justify-center gap-2 text-center font-heading text-[21px] font-extrabold uppercase leading-[1.15] tracking-[-0.3px] text-gold md:text-[32px]" data-id="element-241" data-mp="5qyy2zp6cbf2">
            <GiftIcon className="h-6 w-6 shrink-0 md:h-8 md:w-8" strokeWidth={2.5} aria-hidden data-id="element-242" data-mp="paweyzwr2oln" />
            Plus, receive 5 free professional bonuses
          </h2>
          <p className="mx-auto mt-1.5 max-w-[560px] text-center text-[14px] leading-[1.4] text-onNavyMuted md:text-[15px]" data-id="element-243" data-mp="fdrquujcgj27">
            Five additional specialized resources that complement the six main volumes.
          </p>
          <div className="mt-3 space-y-2.5" data-id="element-244" data-mp="6k6cvdf075tu">
            {bonuses.map(b => <BonusCard key={b.number} {...b} showReferencePrice={bonusReferencePricesEnabled} data-id="element-245" data-mp="beivxmckif52" />)}
          </div>
        </section>

        <BenefitsGrid data-id="element-246" data-mp="1mwpe3b6d48n" />
        <OfferSection showReferencePrices={bonusReferencePricesEnabled} guaranteeEnabled={guaranteeEnabled} data-id="element-247" data-mp="1md3kbielvpc" />
        {guaranteeEnabled && <GuaranteeSection data-id="element-248" data-mp="3jtktpqyehnd9" />}
        <TeachingSituations data-id="element-249" data-mp="3nud3qmf3jwal" />
        {testimonialsEnabled && <TestimonialsSection data-id="element-250" data-mp="3nt33proooe1b" />}
        <FAQAccordion data-id="element-251" data-mp="3nwh8f6ktkzbx" />
        <FinalCTA guaranteeEnabled={guaranteeEnabled} data-id="element-252" data-mp="3gqwuk7v2j4vh" />
        <Footer data-id="element-253" data-mp="3gqeo6ieoza9u" />
      </main>
      {salesPopupEnabled && <PurchasePopup data-id="element-254" data-mp="3brjweqyqybdl" />}
    </div>;
}